import {KycStatus, VerificationStatus} from 'web-angular-client';

/**
 * Display state for a KYC or AVS verification card on the ecommerce landing page. VERIFIED cards
 * are hidden by the landing page rather than rendered; ERROR is shown when the status could not
 * be loaded.
 */
export type VerificationCardState = 'NOT_STARTED' | 'UNDER_REVIEW' | 'FAILED' | 'VERIFIED' | 'ERROR';

/**
 * Maps a PENDING/VERIFIED/FAILED status (shared by KycStatus and VerificationStatus) to a card
 * state. A load failure takes precedence and yields ERROR; otherwise any value other than
 * PENDING/VERIFIED/FAILED — including null/undefined, or AVS's UNVERIFIED — means not started.
 *
 * @param status a KYC or banking verification status value, or null/undefined when not yet set
 * @param loadFailed whether the status could not be loaded
 */
function deriveCardState(
    status: KycStatus | VerificationStatus | null | undefined, loadFailed: boolean | undefined): VerificationCardState
{
    if (loadFailed)
    {
        return 'ERROR';
    }
    switch (status)
    {
        case 'PENDING':
            return 'UNDER_REVIEW';
        case 'VERIFIED':
            return 'VERIFIED';
        case 'FAILED':
            return 'FAILED';
        default:
            return 'NOT_STARTED';
    }
}

/**
 * Maps a KYC status to a verification card state. E-commerce signups leave the KYC status
 * unset until the merchant submits their verification, so an absent status means not started.
 *
 * @param kycStatus the business's KYC status, or null/undefined when verification has not been submitted
 * @param loadFailed whether the KYC details could not be loaded
 */
export function deriveKycCardState(
    kycStatus: KycStatus | null | undefined, loadFailed?: boolean): VerificationCardState
{
    return deriveCardState(kycStatus, loadFailed);
}

/**
 * Maps a banking verification status to a verification card state. AVS runs off the banking
 * details submitted with KYC, so an absent or unverified status means not started.
 *
 * @param verificationStatus the bank details verification status, or null/undefined when absent
 * @param loadFailed whether the banking details could not be loaded
 */
export function deriveAvsCardState(
    verificationStatus: VerificationStatus | null | undefined, loadFailed?: boolean): VerificationCardState
{
    return deriveCardState(verificationStatus, loadFailed);
}
